import React from 'react';

import { DealType, RealtyObjectType, Rooms } from '@search/filter-enums/enums';
import { OfferRoute, useRouter, SearchRoute } from '@search/offer-search-routes/src';
import { useGeoRootRegion } from '@search/geo/src/GeoRootRegionProvider';
import { seoSearch } from '../../../../seo/seoSearch';
import { useSearchOffersLoader } from './index';

export function useRelatedOffers() {
    const loadOffers = useSearchOffersLoader();
    const router = useRouter();
    const offerRoute = router.route(OfferRoute);
    const p = offerRoute.params();
    const regionId = p.region ?? 3;
    const realtyObject = p.categoryType ? enumToGqlRequired(RealtyObjectType, p.categoryType) : 'FLAT';

    const resp = loadOffers({
        regionId,
        nbId: 0,
        realtyObject,
        filters: {
            rooms: p.room ? [ enumToGqlRequired(Rooms, p.room) ] : undefined,
            dealType: p.dealType === DealType.RENT ? 'RENT' : 'SELL',
            realtyObject,
            regionId
        },
        paging: {
            pageNumber: 1,
            pageSize: 10
        },
        skipLastSearch: true,
        limit: 1,
        onlyUnique: false,
        skipSeoCloudBack: true
    });

    const searchRoute = router.route(SearchRoute);

    const goToRelativeOffers = React.useCallback(() => {
        searchRoute.push({
            region: regionId,
            categoryType: p.categoryType,
            dealType: p.dealType,
            rooms: p.room ? [ p.room ] : undefined
        });
    }, [ searchRoute, regionId, p.categoryType, p.dealType ]);

    const rootRegions = useGeoRootRegion();
    const rootRegion = rootRegions.byId(regionId);

    const { relativeTitle } = seoSearch({
        rootRegion,
        params: {
            region: regionId,
            categoryType: p.categoryType,
            dealType: p.dealType,
            segments: {
                rooms: p.room ? [ p.room ] : undefined
            }
        },
        region: rootRegion,
        geos: [],
        offersCount: resp.data?.searchOffersSummary?.count ?? 0
    });

    const data = resp.data;

    return {
        goToRelativeOffers,
        relativeTitle,
        hasOfferWithoutVas: data?.userDetails?.hasOfferWithoutVas ?? false,
        userId: data?.userDetails?.userId ?? '',
        userPhone: data?.userDetails?.phone ?? '',
        mortgageBannerMinutesVariant: data?.mortgageBannerMinutesVariant.value ?? 0,
        mortgageBannerTextVariant: data?.mortgageBannerTextVariant.value ?? 0,
        searchOffers: data?.searchOffers.items,
        count: data?.searchOffersSummary?.count
    };
}

function enumToGqlRequired<Enum extends Object>(all: Enum, input: Enum[keyof Enum]): keyof Enum {
    const keys = Object.keys(all) as unknown as (keyof Enum)[];

    for (const key of keys) {
        if (all[key] === input) return key;
    }

    throw new Error('Unknown enum: ' + input);
}
